import { useState, useEffect } from 'react';
import supabase from '../supabase';
import { theme, font, inputStyle, labelStyle, outlineButton, ghostButton, pillButton } from './adminTheme';
import { useAdminLang } from './AdminLangContext';
import '../design-system.css';

const T = {
    fr: {
        defaultLinkText: 'En savoir plus',
        existing: 'Bandeaux existants',
        loading: 'Chargement...',
        empty: 'Aucun bandeau.',
        active: '· actif',
        deactivate: 'Désactiver',
        activate: 'Activer',
        edit: 'Éditer',
        delete: 'Suppr.',
        editingTitle: 'Éditer le bandeau',
        newTitle: 'Nouveau bandeau',
        newInstead: '+ Nouveau plutôt',
        message: 'Message',
        messagePlaceholder: 'Nouvel album disponible',
        link: 'Lien (optionnel)',
        linkText: 'Texte du lien',
        saving: 'Sauvegarde...',
        update: 'Mettre à jour',
        create: 'Créer le bandeau',
        messageRequired: 'Message requis.',
        saveError: (msg) => `Erreur : ${msg}`,
        saved: 'Bandeau sauvegardé',
        confirmDelete: 'Supprimer ce bandeau ?',
    },
    en: {
        defaultLinkText: 'Learn more',
        existing: 'Existing banners',
        loading: 'Loading...',
        empty: 'No banners.',
        active: '· active',
        deactivate: 'Deactivate',
        activate: 'Activate',
        edit: 'Edit',
        delete: 'Delete',
        editingTitle: 'Edit banner',
        newTitle: 'New banner',
        newInstead: '+ New instead',
        message: 'Message',
        messagePlaceholder: 'New album available',
        link: 'Link (optional)',
        linkText: 'Link text',
        saving: 'Saving...',
        update: 'Update',
        create: 'Create banner',
        messageRequired: 'Message is required.',
        saveError: (msg) => `Error: ${msg}`,
        saved: 'Banner saved',
        confirmDelete: 'Delete this banner?',
    },
};

function AdminBanners() {
    const { lang } = useAdminLang();
    const t = T[lang] || T.fr;
    const EMPTY_FORM = { id: null, message: '', link_url: '', link_text: t.defaultLinkText };
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [statusMsg, setStatusMsg] = useState('');

    const fetchBanners = async () => {
        setLoading(true);
        const { data, error } = await supabase.from('banners').select('*').order('created_at', { ascending: false });
        if (!error && data) setBanners(data);
        setLoading(false);
    };

    useEffect(() => { fetchBanners(); }, []);

    const handleEdit = (b) => setForm(b);
    const handleNew = () => setForm(EMPTY_FORM);

    const handleSave = async () => {
        if (!form.message.trim()) { setStatusMsg(t.messageRequired); return; }
        setSaving(true);

        const payload = { message: form.message, link_url: form.link_url || null, link_text: form.link_text || t.defaultLinkText };

        let error;
        if (form.id) ({ error } = await supabase.from('banners').update(payload).eq('id', form.id));
        else ({ error } = await supabase.from('banners').insert(payload));

        if (error) setStatusMsg(t.saveError(error.message));
        else {
            setStatusMsg(t.saved);
            handleNew();
            fetchBanners();
        }
        setSaving(false);
    };

    const handleEnable = async (id) => {
        await supabase.from('banners').update({ enabled: false }).neq('id', id);
        await supabase.from('banners').update({ enabled: true }).eq('id', id);
        fetchBanners();
    };

    const handleDisable = async (id) => {
        await supabase.from('banners').update({ enabled: false }).eq('id', id);
        fetchBanners();
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t.confirmDelete)) return;
        await supabase.from('banners').delete().eq('id', id);
        fetchBanners();
    };

    return (
        <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap', alignItems: 'flex-start' }}>

            <div style={{ flex: '1 1 320px', minWidth: '280px' }}>
                <p style={{ ...labelStyle, marginBottom: '14px' }}>{t.existing}</p>

                {loading ? (
                    <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.loading}</p>
                ) : banners.length === 0 ? (
                    <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.empty}</p>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {banners.map(b => (
                            <div key={b.id} style={{
                                padding: '10px 12px', background: theme.card,
                                border: b.enabled ? `1px solid ${theme.text}` : `1px solid ${theme.border}`,
                                borderRadius: 0,
                            }}>
                                <p style={{ margin: '0 0 4px', fontFamily: font, fontSize: '12.5px', color: theme.text }}>
                                    {b.message} {b.enabled && <span style={{ color: theme.textMuted, fontSize: '10px' }}>{t.active}</span>}
                                </p>
                                {b.link_url && <p style={{ margin: '0 0 8px', fontFamily: font, fontSize: '10.5px', color: theme.textMuted }}>→ {b.link_url}</p>}
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    {b.enabled ? (
                                        <button className="wlink" onClick={() => handleDisable(b.id)} style={{ ...outlineButton, padding: '5px 10px', fontSize: '10.5px' }}>{t.deactivate}</button>
                                    ) : (
                                        <button className="wlink" onClick={() => handleEnable(b.id)} style={{ ...outlineButton, padding: '5px 10px', fontSize: '10.5px' }}>{t.activate}</button>
                                    )}
                                    <button className="wlink" onClick={() => handleEdit(b)} style={{ ...outlineButton, padding: '5px 10px', fontSize: '10.5px' }}>{t.edit}</button>
                                    <button className="wlink" onClick={() => handleDelete(b.id)} style={{ ...outlineButton, padding: '5px 10px', fontSize: '10.5px', color: theme.textMuted }}>{t.delete}</button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div style={{ flex: '1 1 360px', minWidth: '300px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                    <p style={{ ...labelStyle, marginBottom: 0 }}>{form.id ? t.editingTitle : t.newTitle}</p>
                    {form.id && <button className="wlink" onClick={handleNew} style={ghostButton}>{t.newInstead}</button>}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div>
                        <label style={labelStyle}>{t.message}</label>
                        <input value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} style={inputStyle} placeholder={t.messagePlaceholder} />
                    </div>
                    <div>
                        <label style={labelStyle}>{t.link}</label>
                        <input value={form.link_url} onChange={e => setForm(f => ({ ...f, link_url: e.target.value }))} style={inputStyle} placeholder="/campaign/2026" />
                    </div>
                    <div>
                        <label style={labelStyle}>{t.linkText}</label>
                        <input value={form.link_text} onChange={e => setForm(f => ({ ...f, link_text: e.target.value }))} style={inputStyle} />
                    </div>

                    <button className="wlink" onClick={handleSave} disabled={saving} style={{ ...pillButton, marginTop: '4px' }}>
                        {saving ? t.saving : form.id ? t.update : t.create}
                    </button>

                    {statusMsg && <p style={{ fontSize: '11px', color: theme.textMuted, fontFamily: font, margin: 0 }}>{statusMsg}</p>}
                </div>
            </div>
        </div>
    );
}

export default AdminBanners;